import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Footer from './Footer';


const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'epub' | 'mobi'>('pdf');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [alreadyPaid, setAlreadyPaid] = useState(false);

  // Get format from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const format = (params.get('format') || 'pdf') as 'pdf' | 'epub' | 'mobi';
    setSelectedFormat(format);
  }, [location.search]);

  // Check if user already paid this session
  useEffect(() => {
    if (sessionStorage.getItem('payment_session_verified')) {
      setAlreadyPaid(true);
    }
  }, []);

  const handlePayment = async () => {
    setEmailError(null);
    setError(null);
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Zadajte platnú e-mailovú adresu.');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch('/api/create-payment', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba pri vytváraní platby');
      localStorage.setItem('customer_email', email);
      sessionStorage.setItem('pending_order_code', data.orderCode);
      window.location.href = data.checkoutUrl;
    } catch (err: any) {
      setError(err.message || 'Nastala chyba. Skúste to znova.');
      setIsLoading(false);
    }
  };

  const formatDetails = {
    pdf: {
      name: 'PDF',
      icon: '📄',
      description: 'Pre tlač a čítanie na počítači. 30 strán.',
      features: ['Ideálne pre tlač', 'Kompatibilné s PC/Mac', 'Pevné formátovanie'],
      color: 'red'
    },
    epub: {
      name: 'EPUB',
      icon: '📱',
      description: 'Pre mobilné zariadenia a tablety.',
      features: ['Prispôsobiteľné písmo', 'Ideálne pre telefóny', 'Reflowable text'],
      color: 'green'
    },
    mobi: {
      name: 'MOBI',
      icon: '📚',
      description: 'Pre Kindle a Amazon čítačky.',
      features: ['Kompatibilné s Kindle', 'Optimalizované pre e-ink', 'Amazon zariadenia'],
      color: 'purple'
    }
  };

  const currentFormat = formatDetails[selectedFormat as keyof typeof formatDetails];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-amber-50 to-sky-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-xl font-bold text-blue-800">← Späť</Link>
            <div className="text-gray-600">Platba</div>
          </div>
        </div>
      </nav>

      {/* Payment Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Dokončite nákup ebooku
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-amber-400 mx-auto rounded-md"></div>
          <p className="text-lg text-gray-600 mt-6">
            Bezpečná platba cez Viva Wallet, okamžitý prístup k ebooku
          </p>
        </div>

        {/* Selected Format Display */}
        <div className="bg-white rounded-md shadow-xl p-8 mb-8 border-2 border-blue-200">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 bg-${currentFormat.color}-100 rounded-full flex items-center justify-center text-3xl`}>
                {currentFormat.icon}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{currentFormat.name} formát</h2>
                <p className="text-gray-600">{currentFormat.description}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">5,99 €</p>
              <p className="text-sm text-gray-500">Jednorázovo</p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            {currentFormat.features.map((feature, index) => (
              <div key={index} className="text-center p-3 bg-gray-50 rounded-md">
                <span className="text-green-600">✅</span>
                <span className="text-sm text-gray-700 ml-2">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Format Selection */}
        <div className="bg-white rounded-md shadow-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Zmeňte formát:</h3>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(formatDetails).map(([format, details]) => (
              <button
                key={format}
                onClick={() => navigate(`/payment?format=${format}`)}
                className={`p-4 rounded-md border-2 transition-all ${
                  selectedFormat === format
                    ? `border-${details.color}-500 bg-${details.color}-50`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-12 h-12 bg-${details.color}-100 rounded-full flex items-center justify-center text-2xl mx-auto mb-2`}>
                  {details.icon}
                </div>
                <p className="font-semibold text-gray-900">{details.name}</p>
                <p className="text-sm text-gray-600">{details.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="bg-gradient-to-r from-blue-100 to-amber-100 rounded-md p-6 mb-8 border border-blue-200">
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-semibold text-gray-900">Cena:</span>
            <span className="text-2xl font-bold text-blue-600">5,99 €</span>
          </div>
          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-600">Formát:</span>
            <span className="font-semibold text-gray-900">{currentFormat.name}</span>
          </div>
          <div className="flex justify-between items-center pt-4 border-t border-blue-200">
            <span className="text-xl font-bold text-gray-900">Celkom:</span>
            <span className="text-3xl font-bold text-blue-600">5,99 €</span>
          </div>
        </div>

        {alreadyPaid ? (
          /* Already paid — show download button */
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium px-4 py-2 rounded-full mb-5">
              ✅ Platba úspešná
            </div>
            <div>
              <button
                onClick={() => navigate('/payment-success')}
                className="px-12 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-xl"
              >
                📥 Stiahnuť zakúpený obsah
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Email Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                E-mail pre zaslanie ebooku
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
                placeholder="vas@email.com"
                className={`w-full px-4 py-3 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all ${
                  emailError ? 'border-red-400 bg-red-50' : 'border-blue-200 bg-white'
                }`}
              />
              {emailError && <p className="mt-1 text-red-500 text-sm">{emailError}</p>}
              <p className="mt-1 text-xs text-gray-500">Na tento e-mail vám zašleme odkaz na stiahnutie ebooku.</p>
            </div>

            {/* Payment Button */}
            <div className="text-center">
              <button
                onClick={handlePayment}
                disabled={isLoading}
                className="px-12 py-4 bg-gradient-to-r from-blue-600 to-amber-500 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? 'Pripravujem platbu...' : 'Zaplatiť 5,99 €'}
              </button>

              {error && (
                <p className="mt-4 text-red-600 text-sm font-medium">{error}</p>
              )}

              <div className="mt-6 flex justify-center items-center gap-4">
                <span className="text-sm text-gray-500">Bezpečná platba:</span>
                <div className="flex gap-2">
                  <span className="text-2xl">💳</span>
                  <span className="text-2xl">🔒</span>
                  <span className="text-2xl">✅</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Trust Indicators */}
        <div className="mt-12 text-center">
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-2xl mx-auto mb-2">
                🔒
              </div>
              <p className="text-sm font-semibold text-gray-900">Bezpečná platba</p>
              <p className="text-xs text-gray-600">SSL šifrovanie</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-2xl mx-auto mb-2">
                ⚡
              </div>
              <p className="text-sm font-semibold text-gray-900">Okamžitý prístup</p>
              <p className="text-xs text-gray-600">Po platbe</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-2xl mx-auto mb-2">
                📥
              </div>
              <p className="text-sm font-semibold text-gray-900">Stiahnutie</p>
              <p className="text-xs text-gray-600">PDF, EPUB, MOBI</p>
            </div>
          </div>
        </div>

        {/* Terms */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Platbou súhlasíte s našimi{' '}
            <Link to="/terms-and-conditions" className="text-blue-600 hover:underline">
              Zmluvnými podmienkami
            </Link>{' '}
            a{' '}
            <Link to="/ochrana-sukromia" className="text-blue-600 hover:underline">
              Ochranou súkromia
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentPage;
